import { memo, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Cities from "./Cities.js";
import { formatPercent } from "../../utils/formatNumber";

/* =========================================================
   COUNTRY VIEW CONFIG
========================================================= */

const COUNTRY_VIEW = {
  Turkey: { center: [39, 35], zoom: 6 },
  Greece: { center: [39, 22], zoom: 6 },
  Germany: { center: [51, 10.5], zoom: 6 },
  France: { center: [46.5, 2.5], zoom: 6 },
  "United Kingdom": { center: [54.5, -3.5], zoom: 6 },
  UK: { center: [54.5, -3.5], zoom: 6 },
};

/* =========================================================
   NORMALIZE (UNICODE SAFE + TR + DE)
========================================================= */

function norm(str = "") {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replaceAll("ı", "i")
    .replaceAll("ğ", "g")
    .replaceAll("ü", "u")
    .replaceAll("ş", "s")
    .replaceAll("ö", "o")
    .replaceAll("ç", "c")
    .replaceAll("ß", "ss")
    .replaceAll("é", "e")
    .replaceAll("è", "e")
    .replaceAll("ê", "e")
    .replaceAll("à", "a")
    .replaceAll("â", "a")
    .replaceAll("î", "i")
    .replaceAll("ô", "o")
    .replaceAll("û", "u")
    .replace(/\s+/g, " ")
    .trim();
}

/* =========================================================
   COLOR SCALE
========================================================= */

function getCityColor(value, max) {
  const p = value / max;
  if (p > 0.6) return "#dc2626";   // very high - dark red (hottest)
  if (p > 0.35) return "#f97316";  // high - orange
  if (p > 0.18) return "#fbbf24";  // medium - amber/yellow
  if (p > 0.08) return "#fde047";  // low - bright yellow
  return "#fef08a";                // very low - pale yellow (more visible)
}

/* =========================================================
   COMPONENT
========================================================= */

function CityMap({ country, cities = [] }) {
  const max = useMemo(
    () => Math.max(...cities.map((c) => c.value || 0), 1),
    [cities]
  );

  const view = COUNTRY_VIEW[country] || { center: [20, 0], zoom: 2 };

  /* Prepare marker data with coordinates */
  const markers = useMemo(() => {
    return cities
      .map((city) => {
        const key = norm(city.key);
        const coords = Cities[key];

        if (!coords) {
          // Only log in development mode to reduce console noise
          if (process.env.NODE_ENV === 'development') {
            console.debug("[CityMap] Missing coords:", city.key, "→", key);
          }
          return null;
        }

        const radius = 8 + (city.value / max) * 16;
        const color = getCityColor(city.value, max);

        return {
          name: city.key,
          position: [coords[1], coords[0]], // Leaflet uses [lat, lng]
          value: city.value,
          radius,
          color,
        };
      })
      .filter(Boolean);
  }, [cities, max]);

  return (
    <div style={{ width: "100%", height: 420, position: "relative" }}>
      {/* LEGEND */}
      <div
        style={{
          position: "absolute",
          bottom: 14,
          left: 14,
          background: "white",
          padding: "10px 12px",
          borderRadius: 12,
          fontSize: 12,
          boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
          zIndex: 1000,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6, fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif" }}>
          City intensity 
        </div>
        <LegendRow color="#dc2626" label="Very high" />
        <LegendRow color="#f97316" label="High" />
        <LegendRow color="#fbbf24" label="Medium" />
        <LegendRow color="#fde047" label="Low" />
        <LegendRow color="#fef08a" label="Very low" />
      </div>

      <MapContainer
        center={view.center}
        zoom={view.zoom}
        style={{ height: "100%", width: "100%", borderRadius: 8 }}
        scrollWheelZoom={true}
        zoomControl={true}
        key={country} // Force remount when country changes
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {markers.map((marker) => (
          <CircleMarker
            key={marker.name}
            center={marker.position}
            radius={marker.radius}
            pathOptions={{
              fillColor: marker.color,
              fillOpacity: 0.85,
              color: "#fff",
              weight: 2,
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
              <strong>{marker.name}</strong> ({country})
              <br />
              Share: {formatPercent(marker.value, 1)}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

/* =========================================================
   LEGEND ITEM
========================================================= */

function LegendRow({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
      <span>{label}</span>
    </div>
  );
}

export default memo(CityMap);
